import React, { useState, useMemo } from "react";
import { 
  Search, 
  Send, 
  Paperclip, 
  MoreVertical, 
  Circle, 
  CheckCheck, 
  Plus, 
  Filter, 
  Users,
  User as UserIcon,
  MessageSquare,
  ChevronLeft,
  Info,
  Calendar,
  X
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import InputField from "../../components/common/InputField";

// --- Dummy Data ---

// --- Dummy Data ---
const CONVERSATIONS = [
  {
    id: 1,
    name: "Alex Thompson",
    role: "Student",
    avatar: "https://i.pravatar.cc/150?u=alex",
    lastMessage: "I finished the module 3 assignment, can you review?",
    time: "2m ago",
    unread: 2,
    online: true,
    type: "direct"
  },
  {
    id: 2,
    name: "Modern Web Design Masterclass",
    role: "Broadcast",
    avatar: null,
    lastMessage: "Next live session is scheduled for Friday at 4 PM.",
    time: "1h ago",
    unread: 0,
    online: false,
    type: "broadcast"
  },
  {
    id: 3,
    name: "Sarah Chen",
    role: "Student",
    avatar: "https://i.pravatar.cc/150?u=sarah",
    lastMessage: "Thank you for the feedback on my typography project!",
    time: "3h ago",
    unread: 0,
    online: true,
    type: "direct"
  },
  {
    id: 4,
    name: "James Wilson",
    role: "Student",
    avatar: "https://i.pravatar.cc/150?u=james",
    lastMessage: "I'm having trouble with the framer-motion install.",
    time: "1d ago",
    unread: 0,
    online: false,
    type: "direct"
  },
  {
    id: 5,
    name: "Advanced Interactions",
    role: "Broadcast",
    avatar: null,
    lastMessage: "New resource uploaded: Interaction Design Checklist.",
    time: "2d ago",
    unread: 0,
    online: false,
    type: "broadcast"
  }
];

const MESSAGES = [
  { id: 1, text: "Hi Sarah! I've reviewed your latest project.", sender: "me", time: "10:30 AM" },
  { id: 2, text: "The spacing is much better, but let's look at the color contrast in the header.", sender: "me", time: "10:31 AM" },
  { id: 3, text: "Thank you for the feedback on my typography project! I'll update it right away.", sender: "them", time: "11:45 AM" },
  { id: 4, text: "Great! Let me know when the new version is uploaded.", sender: "me", time: "11:50 AM" },
];

// --- Sub-components ---

const GlassCard = ({ children, className = "" }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const MessageModal = ({ onClose, initialRecipient = "All Students" }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    className="cht-fixed cht-inset-0 cht-z-[150] cht-flex cht-items-center cht-justify-center cht-p-4 md:cht-p-6"
  >
    <div className="cht-fixed cht-inset-0 cht-bg-slate-900/60 cht-backdrop-blur-md" onClick={onClose} />
    <GlassCard className="cht-relative cht-w-full cht-max-w-xl cht-rounded-[40px] cht-p-10 cht-space-y-8 cht-bg-white">
      <div className="cht-flex cht-items-center cht-justify-between">
        <div className="cht-space-y-1">
          <h3 className="cht-text-2xl cht-font-black cht-text-black cht-tracking-tight">
            {initialRecipient === "All Students" ? "Broadcast Message" : "Direct Message"}
          </h3>
          <p className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
            {initialRecipient === "All Students" ? "Send to all enrolled students" : `Recipient: ${initialRecipient}`}
          </p>
        </div>
        <button onClick={onClose} className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-bg-gray-50 cht-rounded-full hover:cht-bg-gray-100 transition-colors">
          <X size={20} />
        </button>
      </div>

      <div className="cht-space-y-6">
        {initialRecipient === "All Students" && (
          <div className="cht-space-y-2">
            <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-2">Target Audience</label>
            <select className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-2xl cht-p-4 cht-text-sm cht-font-bold focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all">
              <option>All Enrolled Students</option>
              <option>Modern Web Design Masterclass Only</option>
              <option>Advanced Interactions Only</option>
              <option>Students with Progress &lt; 50%</option>
            </select>
          </div>
        )}

        <InputField label="Subject" placeholder="e.g. Update regarding your course" />
        
        <div className="cht-space-y-2">
          <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-2">Message Content</label>
          <textarea 
            className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-2xl cht-p-6 cht-text-sm cht-font-medium cht-min-h-[150px] focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
            placeholder="Type your message here..."
          />
        </div>
      </div>

      <div className="cht-flex cht-gap-4">
        <Button variant="ghost" onClick={onClose} className="cht-flex-1">Cancel</Button>
        <Button variant="primary" className="cht-flex-1 cht-rounded-xl" leftIcon={<Send size={18} />}>Send Message</Button>
      </div>
    </GlassCard>
  </motion.div>
);

const InstructorMessages = () => {
  const [selectedId, setSelectedId] = useState(1);
  const [messageText, setMessageText] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, direct, broadcast
  const [searchQuery, setSearchQuery] = useState("");
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastRecipient, setBroadcastRecipient] = useState("All Students");

  const activeConversation = useMemo(() => 
    CONVERSATIONS.find(c => c.id === selectedId), 
  [selectedId]);

  const filteredConversations = useMemo(() => {
    return CONVERSATIONS.filter(c => {
      const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filterType === "all" || c.type === filterType;
      return matchesSearch && matchesFilter;
    });
  }, [searchQuery, filterType]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;
    // Logic for sending
    setMessageText("");
  };

  return (
    <div className="cht-h-[calc(100vh-120px)] cht-flex cht-flex-col cht-space-y-6 cht-animate-in cht-fade-in cht-duration-700">
      <header className="cht-flex cht-items-center cht-justify-between">
        <div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">Messages</h1>
          <p className="cht-text-gray-500 cht-font-medium">Direct communication and student announcements.</p>
        </div>
        <Button 
          variant="primary" 
          leftIcon={<Plus size={18} />} 
          className="cht-rounded-2xl"
          onClick={() => {
            setBroadcastRecipient("All Students");
            setShowBroadcast(true);
          }}
        >
          New Broadcast
        </Button>
      </header>

      <div className="cht-flex-1 cht-flex cht-gap-6 cht-overflow-hidden">
        {/* Left Column: Conversation List */}
        <GlassCard className="cht-w-full md:cht-w-[400px] cht-rounded-[32px] cht-flex cht-flex-col cht-overflow-hidden">
          <div className="cht-p-6 cht-space-y-4">
            <div className="cht-relative">
              <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" size={16} />
              <input 
                type="text" 
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3 cht-pl-12 cht-pr-4 cht-text-xs cht-font-bold focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
              />
            </div>
            <div className="cht-flex cht-gap-2">
              {["all", "direct", "broadcast"].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`cht-flex-1 cht-py-2 cht-rounded-lg cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${
                    filterType === type ? "cht-bg-[#3e57da] cht-text-white" : "cht-bg-gray-50 cht-text-gray-400"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="cht-flex-1 cht-overflow-y-auto cht-px-4 cht-pb-4 cht-space-y-1">
            {filteredConversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => setSelectedId(conv.id)}
                className={`cht-w-full cht-p-4 cht-rounded-2xl cht-flex cht-items-center cht-gap-4 cht-transition-all ${
                  selectedId === conv.id ? "cht-bg-[#3e57da]/5 cht-ring-1 cht-ring-[#3e57da]/10" : "hover:cht-bg-gray-50"
                }`}
              >
                <div className="cht-relative cht-shrink-0">
                  {conv.avatar ? (
                    <div className="cht-w-12 cht-h-12 cht-rounded-xl cht-overflow-hidden">
                      <img src={conv.avatar} alt={conv.name} className="cht-w-full cht-h-full cht-object-cover" />
                    </div>
                  ) : (
                    <div className="cht-w-12 cht-h-12 cht-rounded-xl cht-bg-[#3e57da]/10 cht-flex cht-items-center cht-justify-center cht-text-[#3e57da]">
                      <MessageSquare size={20} />
                    </div>
                  )}
                  {conv.online && (
                    <div className="cht-absolute -cht-top-1 -cht-right-1 cht-w-3.5 cht-h-3.5 cht-bg-green-500 cht-border-2 cht-border-white cht-rounded-full" />
                  )}
                </div>
                <div className="cht-flex-1 cht-text-left cht-min-w-0">
                  <div className="cht-flex cht-justify-between cht-items-start">
                    <h4 className={`cht-text-sm cht-font-black cht-truncate ${selectedId === conv.id ? "cht-text-[#3e57da]" : "cht-text-gray-900"}`}>
                      {conv.name}
                    </h4>
                    <span className="cht-text-[9px] cht-font-bold cht-text-gray-400 cht-uppercase">{conv.time}</span>
                  </div>
                  <p className="cht-text-xs cht-text-gray-400 cht-truncate cht-mt-0.5">{conv.lastMessage}</p>
                </div>
                {conv.unread > 0 && (
                  <div className="cht-w-5 cht-h-5 cht-bg-[#3e57da] cht-text-white cht-text-[9px] cht-font-black cht-rounded-lg cht-flex cht-items-center cht-justify-center">
                    {conv.unread}
                  </div>
                )}
              </button>
            ))}
          </div>
        </GlassCard>

        {/* Right Column: Chat Window */}
        <GlassCard className="cht-flex-1 cht-rounded-[32px] cht-flex cht-flex-col cht-overflow-hidden">
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="cht-p-6 cht-border-b cht-border-gray-50 cht-flex cht-items-center cht-justify-between">
                <div className="cht-flex cht-items-center cht-gap-4">
                  <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-gray-100 cht-overflow-hidden">
                    {activeConversation.avatar ? (
                      <img src={activeConversation.avatar} alt={activeConversation.name} className="cht-w-full cht-h-full cht-object-cover" />
                    ) : (
                      <div className="cht-w-full cht-h-full cht-flex cht-items-center cht-justify-center cht-text-[#3e57da] cht-bg-[#3e57da]/10">
                        <MessageSquare size={18} />
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="cht-text-sm cht-font-black cht-text-gray-900">{activeConversation.name}</h4>
                    <div className="cht-flex cht-items-center cht-gap-2">
                      <Circle size={8} className={activeConversation.online ? "cht-fill-green-500 cht-text-green-500" : "cht-fill-gray-300 cht-text-gray-300"} />
                      <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                        {activeConversation.online ? "Active Now" : "Away"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="cht-flex cht-items-center cht-gap-2">
                  <button className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-bg-gray-50 cht-rounded-xl hover:cht-bg-gray-100 transition-colors">
                    <Info size={18} className="cht-text-gray-400" />
                  </button>
                  <button className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-bg-gray-50 cht-rounded-xl hover:cht-bg-gray-100 transition-colors">
                    <MoreVertical size={18} className="cht-text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Message History */}
              <div className="cht-flex-1 cht-overflow-y-auto cht-p-8 cht-space-y-6">
                <div className="cht-flex cht-justify-center cht-mb-10">
                  <span className="cht-px-4 cht-py-1.5 cht-bg-gray-50 cht-rounded-full cht-text-[9px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    October 24, 2023
                  </span>
                </div>

                {MESSAGES.map((msg) => (
                  <div key={msg.id} className={`cht-flex ${msg.sender === 'me' ? 'cht-justify-end' : 'cht-justify-start'}`}>
                    <div className={`cht-max-w-[70%] cht-space-y-1.5`}>
                      <div className={`cht-p-4 cht-rounded-2xl cht-text-sm cht-font-medium ${
                        msg.sender === 'me' 
                          ? "cht-bg-[#3e57da] cht-text-white cht-rounded-tr-none" 
                          : "cht-bg-gray-100 cht-text-gray-800 cht-rounded-tl-none"
                      }`}>
                        {msg.text}
                      </div>
                      <div className={`cht-flex cht-items-center cht-gap-2 ${msg.sender === 'me' ? 'cht-justify-end' : 'cht-justify-start'}`}>
                        <span className="cht-text-[9px] cht-font-bold cht-text-gray-400">{msg.time}</span>
                        {msg.sender === 'me' && <CheckCheck size={12} className="cht-text-[#3e57da]" />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="cht-p-6 cht-bg-white/50 cht-backdrop-blur-md">
                <form onSubmit={handleSend} className="cht-relative">
                  <div className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-flex cht-items-center cht-gap-2">
                    <button type="button" className="cht-w-8 cht-h-8 cht-flex cht-items-center cht-justify-center cht-text-gray-400 hover:cht-text-[#3e57da] transition-colors">
                      <Paperclip size={18} />
                    </button>
                  </div>
                  <input 
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type your message..."
                    className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-2xl cht-py-4 cht-pl-14 cht-pr-14 cht-text-sm cht-font-medium focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
                  />
                  <div className="cht-absolute cht-right-2 cht-top-1/2 -cht-translate-y-1/2">
                    <button 
                      type="submit"
                      disabled={!messageText.trim()}
                      className={`cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-transition-all ${
                        messageText.trim() ? "cht-bg-[#3e57da] cht-text-white cht-shadow-lg" : "cht-bg-gray-100 cht-text-gray-300"
                      }`}
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </form>
              </div>
            </>
          ) : (
            <div className="cht-flex-1 cht-flex cht-items-center cht-justify-center cht-text-center cht-p-10">
              <div className="cht-space-y-4">
                <div className="cht-w-20 cht-h-20 cht-bg-gray-50 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-mx-auto cht-text-gray-200">
                  <MessageSquare size={32} />
                </div>
                <h3 className="cht-text-xl cht-font-black cht-text-gray-900">Select a conversation</h3>
                <p className="cht-text-sm cht-text-gray-400 cht-font-medium">Choose a student or broadcast group from the list to start messaging.</p>
              </div>
            </div>
          )}
        </GlassCard>
      </div>

      <AnimatePresence>
        {showBroadcast && (
          <MessageModal 
            onClose={() => setShowBroadcast(false)} 
            initialRecipient={broadcastRecipient}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default InstructorMessages;
